import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors and stale cache
api.interceptors.response.use(
  (response) => {
    // Detect if CDN returned HTML instead of JSON (stale cache)
    const ct = response.headers?.['content-type'] || '';
    if (ct.includes('text/html') && response.config?.responseType !== 'blob') {
      const lastReload = sessionStorage.getItem('last_cache_reload');
      const now = Date.now();
      if (!lastReload || (now - parseInt(lastReload)) > 10000) {
        sessionStorage.setItem('last_cache_reload', now.toString());
        window.location.reload();
      }
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Helper to format API errors
export const formatApiError = (error) => {
  // Network error (CORS blocked, no connection, etc.)
  if (!error.response) {
    if (error.message === 'Network Error') {
      return 'Errore di connessione al server. Prova a ricaricare la pagina.';
    }
    return error.message || 'Errore di rete. Riprova.';
  }
  const detail = error.response?.data?.detail;
  if (detail == null) return 'Si è verificato un errore. Riprova.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  }
  if (detail?.msg) return detail.msg;
  return String(detail);
};

// Auth API
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  createUser: (data) => api.post('/auth/users', data),
  listUsers: () => api.get('/auth/users')
};

// Departments API
export const departmentsApi = {
  list: () => api.get('/departments'),
  get: (id) => api.get(`/departments/${id}`),
  create: (data) => api.post('/departments', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`)
};

// Doctors API
export const doctorsApi = {
  list: (departmentId) => api.get('/doctors', { params: { department_id: departmentId } }),
  get: (id) => api.get(`/doctors/${id}`),
  create: (data) => api.post('/doctors', data),
  update: (id, data) => api.put(`/doctors/${id}`, data),
  delete: (id) => api.delete(`/doctors/${id}`)
};

// Patients API
export const patientsApi = {
  list: (search) => api.get('/patients', { params: { search } }),
  get: (id) => api.get(`/patients/${id}`),
  getByPhone: (phone) => api.get(`/patients/by-phone/${phone}`),
  create: (data) => api.post('/patients', data),
  update: (id, data) => api.put(`/patients/${id}`, data),
  delete: (id) => api.delete(`/patients/${id}`),
  uploadCsv: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/patients/upload-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Appointments API
export const appointmentsApi = {
  list: (params) => api.get('/appointments', { params }),
  get: (id) => api.get(`/appointments/${id}`),
  create: (data) => api.post('/appointments', data),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  delete: (id) => api.delete(`/appointments/${id}`),
  getAvailableSlots: (doctorId, date) => api.get(`/appointments/available-slots/${doctorId}`, { params: { date } })
};

// Call Logs API
export const callLogsApi = {
  list: (params) => api.get('/call-logs', { params }),
  get: (id) => api.get(`/call-logs/${id}`),
  delete: (id) => api.delete(`/call-logs/${id}`),
  bulkAction: (action, ids) => api.post('/call-logs/bulk-action', { action, ids })
};

// VoIP Test API
export const voipTestApi = {
  startCall: (callerPhone) => api.post('/voip/test/start', { caller_phone: callerPhone }),
  sendMessage: (callId, message) => api.post('/voip/test/message', { call_id: callId, message }),
  endCall: (callId) => api.post('/voip/test/end', { call_id: callId }),
  generateTTS: (text) => api.post('/voip/test/tts', { text }),
  getVoices: () => api.get('/voip/voices'),
  previewVoice: (voiceId) => api.post('/voip/voices/preview', { voice_id: voiceId }),
  selectVoice: (voiceId) => api.post('/voip/voices/select', { voice_id: voiceId }),
  cloneVoice: (formData) => api.post('/voip/voices/clone', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  listClonedVoices: () => api.get('/voip/voices/cloned'),
  deleteClonedVoice: (voiceId) => api.delete(`/voip/voices/cloned/${voiceId}`)
};

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats')
};

// Settings API
export const settingsApi = {
  getVoip: () => api.get('/settings/voip'),
  updateVoip: (data) => api.put('/settings/voip', data)
};

// Waitlist API
export const waitlistApi = {
  list: (params) => api.get('/waitlist', { params }),
  stats: () => api.get('/waitlist/stats'),
  add: (data) => api.post('/waitlist', data),
  update: (id, data) => api.put(`/waitlist/${id}`, data),
  remove: (id) => api.delete(`/waitlist/${id}`),
  convert: (id, data) => api.post(`/waitlist/${id}/convert`, data)
};

export default api;
